// Shared section->problem name-matching, used by the vJSON block editor
// (`json-section-editor-wrapper.tsx`) and the markdown-native section-identity
// anchoring path in `coding-overlay.ts`.

/** Minimal shape either `SessionProblem` or `CodingOverlayProblem` satisfies. */
export interface ProblemNameMatchCandidate {
  name: string;
}

export const normalizeName = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[–—]/g, '-');

/**
 * Matches a note section to the problem(s) it belongs to, trying (in order):
 * an exact name match on the section title, the title with a numeric prefix
 * stripped, an unambiguous substring containment match, and finally a legacy
 * "ProblemName:" heading inside the section body.
 */
export const findProblemsForSection = <TProblem extends ProblemNameMatchCandidate>({
  sectionKey,
  sectionContent,
  problems,
}: {
  sectionKey: string;
  sectionContent: string;
  problems: TProblem[] | undefined;
}): TProblem[] | undefined => {
  if (!problems?.length) return undefined;

  const normalizedKey = normalizeName(sectionKey);

  // Strategy 1: Exact normalized match — section title IS the problem name (new PBC format).
  const byExactKey = problems.filter((p) => normalizeName(p.name) === normalizedKey);
  if (byExactKey.length > 0) return byExactKey;

  // Strategy 2: Strip leading "N." / "N)" numeric prefix from section key, then exact match.
  const strippedKey = normalizedKey.replace(/^\d+[\.)]\s*/, '');
  if (strippedKey !== normalizedKey) {
    const byStrippedKey = problems.filter((p) => normalizeName(p.name) === strippedKey);
    if (byStrippedKey.length > 0) return byStrippedKey;
  }

  // Strategy 3: Substring containment — handles cases where one side has extra qualifiers
  // (e.g. section key "Hypertension" ↔ problem name "Essential Hypertension").
  // Only apply when the result is unambiguous (exactly one match).
  const byContains = problems.filter((p) => {
    const normalizedName = normalizeName(p.name);
    return (
      normalizedKey.includes(normalizedName) || normalizedName.includes(normalizedKey)
    );
  });
  if (byContains.length === 1) return byContains;

  // Strategy 4: Legacy content heading — "ProblemName:\n" in markdown content.
  if (!sectionContent) return undefined;
  const byContent = problems.filter((p) => {
    const escaped = p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`^${escaped}:\\s*$`, 'im').test(sectionContent);
  });
  return byContent.length > 0 ? byContent : undefined;
};
